import React, { FC, useState, useContext } from 'react';
import clsx from 'clsx';
import { Spinner, ButtonWithIconAndLoader } from '@td-client-frontend/common';
import {
  useOrganizationProfiles,
  ProfileType,
  useAssignProfile,
  useMarketingProfileCache
} from '../../../hooks/useMarketingProfile';
import Modal from '../../Modal/NewModal';
import ProfileSelect from './ProfileSelect';
import { MarketingProfileById, MarketingHierarchyRowData } from '../../../@types/Marketing';
import { SaveModalContent } from '../SaveModal/index';
import { Button } from '../../Button';
import { Nullable } from '../../../@types/common';
import { PendingChangesContext } from '../../../context/PendingChangesContext';
import { useMarketingSearchCache } from '../../../hooks/useMarketingPreferences';

interface Props {
  profileData: MarketingProfileById;
  isOpen: boolean;
  setOpen: (x: boolean) => void;
  groupOrgData: Nullable<MarketingHierarchyRowData>;
}

export const ChangeAssignedProfileModal: FC<Props> = ({ profileData, isOpen, setOpen, groupOrgData }) => {
  const [affectedGroupsLoading, setAffectedGroupsLoading] = useState<boolean>(false);
  const [assignedProfile, setProfile] = useState<Nullable<MarketingProfileById>>(profileData);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const { showPrompt, checkAndPrompt } = useContext(PendingChangesContext);

  const { assignProfile, isLoading } = useAssignProfile(groupOrgData?.id || '');
  const { invalidateMarketingSearchCache } = useMarketingSearchCache();
  const { data, isLoading: organizationProfileLoading } = useOrganizationProfiles(groupOrgData?.id || '');
  const { setProfileCache } = useMarketingProfileCache();

  const onChangeAssignedProfile = async (profileId: string) => {
    await assignProfile(profileId);
    invalidateMarketingSearchCache();
    setProfileCache(profileData.id, null);
    setOpen(false);
  };

  const handleAffectedGroupsLoading = (state: boolean) => setAffectedGroupsLoading(state);

  const handleClose = () => {
    if (!isLoading && !showPrompt) {
      setOpen(false);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (showSaveModal) {
      setShowSaveModal(false);
    } else {
      handleClose();
    }
  };

  const handleApply = async () => {
    if (showSaveModal) {
      await checkAndPrompt(async () => {
        await onChangeAssignedProfile(assignedProfile!.id);
        handleClose();
      });
    } else {
      setShowSaveModal(true);
    }
  };

  const onProfileSelect = (profile: MarketingProfileById) => {
    setProfile(profile);
  };

  const CustomButtons = () => (
    <div className="flex gap-4">
      <Button title="Cancel" kind="secondary" onClick={handleCancel} disabled={isLoading} />
      <div className="w-32">
        <ButtonWithIconAndLoader
          title={showSaveModal ? 'Save' : 'Apply'}
          loading={isLoading}
          className="btn-primary flex items-center justify-center btn h-full"
          labelClasses={clsx('text-normal', isLoading && 'hidden')}
          onClick={handleApply}
          disabled={!assignedProfile || affectedGroupsLoading || profileData?.id === assignedProfile.id}
        />
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      header="Change assigned profile"
      customFooterActions={{ right: <CustomButtons /> }}
      size={showSaveModal ? 'medium' : 'auto'}
    >
      {organizationProfileLoading && !data ? (
        <div className="flex justify-center w-full mt-4" data-testid="changeAssignProfileSpinner">
          <span className="mr-2">
            <Spinner />
          </span>
        </div>
      ) : showSaveModal && assignedProfile ? (
        <SaveModalContent
          profile={{ name: assignedProfile.name, id: groupOrgData?.id!, type: ProfileType.ORGANIZATION }}
          onLoadingStateChange={handleAffectedGroupsLoading}
        />
      ) : (
        <div className="p-6 pb-20" data-testid="change-assigned-profile-modal">
          <ProfileSelect
            profileList={data?.availableMarketingProfiles}
            selectedProfile={assignedProfile}
            onSelectProfile={onProfileSelect}
          />
        </div>
      )}
    </Modal>
  );
};
